/** Pure helpers for installing and describing storage agents (the Add agent dialog and the agent page). */

export interface AgentDownloadFile { file: string; os: string; arch: string; size: number; sha256: string }
export interface AgentDownloads { version: string; base: string; files: AgentDownloadFile[] }

const osLabel = (os: string) => (os === 'darwin' ? 'macOS' : os === 'linux' ? 'Linux' : os);
const archLabel = (os: string, arch: string) => (os === 'darwin' && arch === 'arm64' ? 'Apple silicon (arm64)' : os === 'darwin' && arch === 'x64' ? 'Intel (x64)' : arch);
export const targetLabel = (f: { os: string; arch: string }) => `${osLabel(f.os)} · ${archLabel(f.os, f.arch)}`;

/** Which file to preselect from the browser's platform; Apple silicon cannot be told from Intel in a browser, so a Mac gets arm64. */
export function guessFile(files: AgentDownloadFile[], userAgent: string, platform = ''): AgentDownloadFile | undefined {
  const ua = `${userAgent} ${platform}`;
  const want = /Mac/i.test(ua) ? ['darwin-arm64', 'darwin-x64'] : /Linux|X11/i.test(ua) && !/Android/i.test(ua) ? [/aarch64|arm64/i.test(ua) ? 'linux-arm64' : 'linux-x64', 'linux-x64', 'linux-arm64'] : [];
  for (const t of want) {
    const found = files.find((f) => `${f.os}-${f.arch}` === t);
    if (found) return found;
  }
  return files[0];
}

/** Where the program and its prism-agent.conf live; the single-file steps run from here. */
export const agentFolder = (os: string) => (os === 'darwin' ? '~/"PRISM Agent"' : '/opt/prism-agent');

/** Suggested storage and watch folders per OS. */
export function defaultFolders(os: string): { storage: string; watch: string } {
  return os === 'darwin' ? { storage: '~/PRISM Agent/storage', watch: '~/PRISM Agent/watch' } : { storage: '/srv/prism/storage', watch: '/srv/prism/watch' };
}

/** Suggested export root per OS (optional: without one, export folders go under the storage root). */
export const defaultExportFolder = (os: string) => (os === 'darwin' ? '~/PRISM Agent/exports' : '/srv/prism/exports');

const normalise = (p: string) => p.trim().replace(/^~(?=\/|$)/, '$HOME').replace(/\/+$/, '');
const isHome = (p: string) => normalise(p) === '$HOME' || normalise(p).startsWith('$HOME/');

/** Same rule as rootsOverlap in services/storage-agent/src/config.ts: equal, or one inside the other. */
export function foldersOverlap(a: string, b: string): boolean {
  const x = normalise(a);
  const y = normalise(b);
  return x === y || x.startsWith(`${y}/`) || y.startsWith(`${x}/`);
}

/**
 * A message when the folders cannot be used (the agent would refuse them), otherwise null. Storage or watch may be empty,
 * not both; the export folder is optional and must be separate from both.
 */
export function folderProblem(storage: string, watch: string, exportRoot = '', editRoot = ''): string | null {
  const s = storage.trim();
  const w = watch.trim();
  const x = exportRoot.trim();
  const e = editRoot.trim();
  if (!s && !w) return 'Fill in at least one of the two folders.';
  for (const [label, v] of [['Storage folder', s], ['Watch folder', w], ['Export folder', x], ['Edit folder', e]] as const) {
    if (!v) continue;
    if (!(v.startsWith('/') || v === '~' || v.startsWith('~/'))) return `${label} must be a full path, starting with / or ~/.`;
    if (/[\u0000-\u001f!]/.test(v)) return `${label} cannot contain “!” or line breaks.`;
  }
  if (s && w && foldersOverlap(s, w)) return 'The storage and watch folders must be separate: neither can be inside the other.';
  if (x && ((s && foldersOverlap(x, s)) || (w && foldersOverlap(x, w)))) return 'The export folder must be separate from the storage and watch folders: none can be inside another.';
  // The edit folder (1.0.0-rc.6), where editors work, is apart from all three.
  if (e && ((s && foldersOverlap(e, s)) || (w && foldersOverlap(e, w)) || (x && foldersOverlap(e, x)))) return 'The edit folder must be separate from the storage, watch and export folders: none can be inside another.';
  return null;
}

/** A path as one shell word. A leading ~ becomes "$HOME" so the shell, not the agent, expands it, and the rest is safe inside double quotes. */
export function shellPath(path: string): string {
  const t = path.trim();
  const esc = (v: string) => v.replace(/(["\\$`])/g, '\\$1');
  if (t === '~') return '"$HOME"';
  if (t.startsWith('~/')) return `"$HOME/${esc(t.slice(2))}"`;
  return `"${esc(t)}"`;
}

interface InstallStep { id: 'download' | 'configure' | 'install'; title: string; command: string; note: string }

/**
 * The single-file install as separate steps, each pasted on its own: `configure` is interactive in a terminal unless --yes is given,
 * so a block pasted whole would feed its later lines to its questions. With `token` null the configure step has no command yet.
 */
export function installSteps(o: { origin: string; token: string | null; downloadsBase: string; file: AgentDownloadFile; storage: string; watch: string; exportRoot?: string; editRoot?: string; reenrol?: boolean }): InstallStep[] {
  const darwin = o.file.os === 'darwin';
  const url = `${o.downloadsBase.replace(/\/$/, '')}/${o.file.file}`;
  const folders = [o.storage.trim(), o.watch.trim(), (o.exportRoot ?? '').trim(), (o.editRoot ?? '').trim()].filter(Boolean);
  const outside = darwin ? [] : folders.filter((f) => !isHome(f));
  const download = darwin
    ? ['mkdir -p ~/"PRISM Agent" && cd ~/"PRISM Agent"', `curl -fsSL ${url} | gunzip > prism-agent`, 'chmod +x prism-agent && codesign --force --sign - prism-agent']
    : ['sudo mkdir -p /opt/prism-agent && sudo chown "$USER" /opt/prism-agent && cd /opt/prism-agent', `curl -fsSL ${url} | gunzip > prism-agent`, 'chmod +x prism-agent'];
  if (outside.length) {
    const list = outside.map(shellPath).join(' ');
    download.push(`sudo mkdir -p ${list} && sudo chown "$USER" ${list}`);
  }
  const flags = [
    `--url ${o.origin}`,
    // `--token=…`: a token may start with "-", which `--token -…` would read as another option.
    `--token=${o.token ?? '<enrolment token>'}`,
    ...(o.storage.trim() ? [`--storage-root ${shellPath(o.storage)}`] : []),
    ...(o.watch.trim() ? [`--watch-root ${shellPath(o.watch)}`] : []),
    ...(o.exportRoot?.trim() ? [`--export-root ${shellPath(o.exportRoot)}`] : []),
    ...(o.editRoot?.trim() ? [`--edit-root ${shellPath(o.editRoot)}`] : []),
    '--yes',
    ...(o.reenrol ? ['--reenroll'] : []),
  ];
  return [
    {
      id: 'download', title: 'Download the agent',
      command: download.join('\n'),
      note: darwin ? 'Creates the agent folder, downloads the program and signs it so macOS runs it.' : outside.length ? 'Creates the agent folder, downloads the program, and creates the folders below with sudo, owned by you, so the agent can run as your user.' : 'Creates the agent folder and downloads the program.',
    },
    { id: 'configure', title: 'Connect it to PRISM', command: o.token ? `./prism-agent configure ${flags.join(' ')}` : '', note: 'Enrols the agent, creates the folders if they are missing and asks nothing. Run it from the agent folder.' },
    { id: 'install', title: 'Start it', command: darwin ? './prism-agent install' : 'sudo ./prism-agent install', note: 'Installs it as a service that starts with the computer. Instead, use ./prism-agent run to keep it in this terminal, or ./prism-agent start to run it in the background.' },
  ];
}

/** Re-enrol the agent on the computer it already runs on: its folders stay as configured. */
export function reenrolCommand(o: { origin: string; token: string; os: string }): string {
  return [`cd ${agentFolder(o.os)}`, `./prism-agent configure --url ${o.origin} --token=${o.token} --reenroll --yes`, './prism-agent restart'].join('\n');
}

/** Update an agent installed as a single file. */
export const updateCommand = (os: string) => `cd ${agentFolder(os)}\n./prism-agent update`;

export type Packaging = 'binary' | 'docker' | 'source';

/** "Single file · macOS arm64", "Docker", "Source"; null for agents that do not report it. */
export function packagingLabel(caps: { packaging?: Packaging; os?: string; arch?: string }): string | null {
  if (!caps.packaging) return null;
  const where = caps.os ? ` · ${osLabel(caps.os)}${caps.arch ? ` ${caps.arch}` : ''}` : '';
  return `${caps.packaging === 'binary' ? 'Single file' : caps.packaging === 'docker' ? 'Docker' : 'Source'}${where}`;
}

/** How to update an agent that is behind PRISM, by how it is installed. */
export function updateHint(packaging: Packaging | undefined): string | null {
  switch (packaging) {
    case 'binary': return 'run prism-agent update';
    case 'docker': return 'rebuild the container';
    case 'source': return 'git pull and restart';
    default: return null;
  }
}
